import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { clsx } from 'clsx'
import {
  type BookOpen,
  ChevronRight,
  Copy,
  FilePlus2,
  LayoutGrid,
  ListTree,
  MessageSquareText,
  MoreHorizontal,
  RotateCcw,
  RotateCw,
  Scissors,
  Trash2,
  X,
} from 'lucide-react'
import { memo, useEffect, useMemo, useState } from 'react'
import { extractPages } from '@/lib/commands'
import { resolveDest } from '@/lib/pdf/links'
import { allSources } from '@/lib/pdf/sources'
import { useDoc } from '@/store/documentStore'
import { type SidebarTab, useUi } from '@/store/uiStore'
import type { Annotation, PageRef } from '@/types'
import { IconButton } from '../ui/Button'
import { MenuItem, Popover } from '../ui/Popover'
import { PageThumb } from './PageThumb'

const TABS: { id: SidebarTab; label: string; icon: typeof BookOpen }[] = [
  { id: 'pages', label: 'Pages', icon: LayoutGrid },
  { id: 'outline', label: 'Outline', icon: ListTree },
  { id: 'comments', label: 'Notes', icon: MessageSquareText },
]

export function Sidebar({ overlay }: { overlay: boolean }) {
  const tab = useUi((s) => s.sidebarTab)
  const setTab = useUi((s) => s.setSidebarTab)
  const toggle = useUi((s) => s.toggleSidebar)

  return (
    <aside
      aria-label="Sidebar"
      className={clsx(
        'flex h-full w-[248px] shrink-0 flex-col border-line border-r bg-surface',
        overlay && 'absolute inset-y-0 left-0 z-30 animate-in shadow-lg',
      )}
    >
      <div className="flex items-center gap-1 border-line border-b p-2">
        <div role="tablist" className="flex flex-1 gap-1 rounded-xl bg-surface-2 p-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              title={t.label}
              className={clsx(
                'flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 font-medium text-[12px] transition-colors',
                tab === t.id ? 'bg-surface text-fg shadow-sm' : 'text-fg-3 hover:text-fg',
              )}
            >
              <t.icon className="size-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        {overlay && (
          <IconButton label="Close sidebar" size="sm" onClick={() => toggle(false)}>
            <X className="size-4" />
          </IconButton>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'pages' && <PagesList closeOnPick={overlay} />}
        {tab === 'outline' && <Outline closeOnPick={overlay} />}
        {tab === 'comments' && <Comments closeOnPick={overlay} />}
      </div>
    </aside>
  )
}

function PagesList({ closeOnPick }: { closeOnPick: boolean }) {
  const pages = useDoc((s) => s.pages)
  const current = useUi((s) => s.currentPage)
  const reorder = useDoc((s) => s.reorderPages)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const ids = useMemo(() => pages.map((p) => p.id), [pages])

  useEffect(() => {
    document.querySelector(`[data-thumb-index="${current}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [current])

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return
    const from = ids.indexOf(String(e.active.id))
    const to = ids.indexOf(String(e.over.id))
    reorder(arrayMove(ids, from, to))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol className="flex flex-col items-center gap-3 px-3 py-4">
          {pages.map((p, i) => (
            <SortableThumb key={p.id} page={p} index={i} active={i === current} closeOnPick={closeOnPick} />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  )
}

const SortableThumb = memo(function SortableThumb({
  page,
  index,
  active,
  closeOnPick,
}: {
  page: PageRef
  index: number
  active: boolean
  closeOnPick: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
  })
  const doc = useDoc.getState
  return (
    <li
      ref={setNodeRef}
      data-thumb-index={index}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
      }}
      className="group relative flex flex-col items-center gap-1.5"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Go to page ${index + 1}`}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          useUi.getState().goToPage(index)
          if (closeOnPick) useUi.getState().toggleSidebar(false)
        }}
        className={clsx(
          'rounded-md p-[3px] outline-none ring-2 transition-shadow',
          active ? 'ring-accent' : 'ring-transparent hover:ring-line-strong',
          isDragging && 'opacity-80 shadow-lg',
        )}
      >
        <PageThumb page={page} width={150} className="rounded-[3px] shadow-sm" />
      </button>
      <span className={clsx('text-[11px] tabular-nums', active ? 'font-semibold text-accent' : 'text-fg-3')}>
        {index + 1}
      </span>
      <div className="absolute top-1.5 right-1.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100">
        <Popover
          align="end"
          trigger={({ toggle, ref }) => (
            <button
              ref={ref}
              type="button"
              aria-label={`Page ${index + 1} actions`}
              onClick={toggle}
              className="flex size-7 items-center justify-center rounded-lg border border-line bg-surface/95 text-fg-2 shadow-sm hover:text-fg"
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          {(close) => (
            <div className="w-52">
              <MenuItem
                icon={<RotateCw />}
                label="Rotate right"
                onSelect={() => {
                  doc().rotatePages([page.id], 90)
                  close()
                }}
              />
              <MenuItem
                icon={<RotateCcw />}
                label="Rotate left"
                onSelect={() => {
                  doc().rotatePages([page.id], -90)
                  close()
                }}
              />
              <MenuItem
                icon={<Copy />}
                label="Duplicate"
                onSelect={() => {
                  doc().duplicatePages([page.id])
                  close()
                }}
              />
              <MenuItem
                icon={<FilePlus2 />}
                label="Insert blank page after"
                onSelect={() => {
                  doc().insertBlankPage(index + 1)
                  close()
                }}
              />
              <MenuItem
                icon={<Scissors />}
                label="Extract page…"
                onSelect={() => {
                  void extractPages([page.id])
                  close()
                }}
              />
              <MenuItem
                icon={<Trash2 />}
                label="Delete page"
                danger
                disabled={doc().pages.length <= 1}
                onSelect={() => {
                  doc().deletePages([page.id])
                  close()
                }}
              />
            </div>
          )}
        </Popover>
      </div>
    </li>
  )
})

interface OutlineNode {
  title: string
  dest: string | unknown[] | null
  url?: string | null
  items: OutlineNode[]
  bold?: boolean
  italic?: boolean
}

function Outline({ closeOnPick }: { closeOnPick: boolean }) {
  const sources = useDoc((s) => s.sources)
  const [items, setItems] = useState<OutlineNode[] | null>(null)
  const srcId = sources[0]?.id

  useEffect(() => {
    const src = allSources().find((s) => s.info.id === srcId)
    if (!src) return
    let alive = true
    src.pdf.getOutline().then((o) => alive && setItems((o as OutlineNode[] | null) ?? []))
    return () => {
      alive = false
    }
  }, [srcId])

  if (items === null) return <p className="p-4 text-fg-3 text-sm">Loading…</p>
  if (!items.length)
    return (
      <div className="flex flex-col items-center gap-2 p-8 text-center text-fg-3">
        <ListTree className="size-8 opacity-50" />
        <p className="text-sm">This document has no outline.</p>
      </div>
    )
  return (
    <ul className="p-2 text-[13px]">
      {items.map((n, i) => (
        <OutlineItem key={i} node={n} srcId={srcId!} depth={0} closeOnPick={closeOnPick} />
      ))}
    </ul>
  )
}

function OutlineItem({
  node,
  srcId,
  depth,
  closeOnPick,
}: {
  node: OutlineNode
  srcId: string
  depth: number
  closeOnPick: boolean
}) {
  const [open, setOpen] = useState(depth === 0 && node.items.length < 12)
  const go = async () => {
    if (node.url) {
      window.open(node.url, '_blank', 'noopener')
      return
    }
    const r = await resolveDest(srcId, node.dest)
    if (r) {
      useUi.getState().goToPage(r.index, r.top)
      if (closeOnPick) useUi.getState().toggleSidebar(false)
    }
  }
  return (
    <li>
      <div className="flex items-center rounded-lg hover:bg-surface-3" style={{ paddingLeft: depth * 12 }}>
        <button
          type="button"
          aria-label={open ? 'Collapse' : 'Expand'}
          onClick={() => setOpen(!open)}
          className={clsx(
            'flex size-6 shrink-0 items-center justify-center text-fg-3',
            !node.items.length && 'invisible',
          )}
        >
          <ChevronRight className={clsx('size-3.5 transition-transform', open && 'rotate-90')} />
        </button>
        <button
          type="button"
          onClick={go}
          className={clsx(
            'flex-1 truncate py-1.5 pr-2 text-left',
            node.bold && 'font-semibold',
            node.italic && 'italic',
          )}
          title={node.title}
        >
          {node.title}
        </button>
      </div>
      {open && node.items.length > 0 && (
        <ul>
          {node.items.map((c, i) => (
            <OutlineItem key={i} node={c} srcId={srcId} depth={depth + 1} closeOnPick={closeOnPick} />
          ))}
        </ul>
      )}
    </li>
  )
}

const TYPE_LABEL: Record<Annotation['type'], string> = {
  highlight: 'Highlight',
  underline: 'Underline',
  strikeout: 'Strikethrough',
  ink: 'Drawing',
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  line: 'Line',
  arrow: 'Arrow',
  text: 'Text box',
  textEdit: 'Text edit',
  note: 'Note',
  image: 'Image',
}

function annText(a: Annotation) {
  if (a.type === 'note') return a.text
  if (a.type === 'text') return a.text
  if (a.type === 'textEdit') return `“${a.original}” → “${a.text}”`
  if ('text' in a && a.text) return `“${a.text}”`
  return ''
}

function Comments({ closeOnPick }: { closeOnPick: boolean }) {
  const pages = useDoc((s) => s.pages)
  const annotations = useDoc((s) => s.annotations)
  const selection = useUi((s) => s.selection)
  const [filter, setFilter] = useState<'all' | 'notes'>('all')
  const groups = pages
    .map((p, i) => ({
      page: p,
      index: i,
      anns: (annotations[p.id] ?? []).filter((a) =>
        filter === 'notes' ? a.type === 'note' || a.comment : true,
      ),
    }))
    .filter((g) => g.anns.length)
  const total = groups.reduce((n, g) => n + g.anns.length, 0)

  return (
    <div className="p-2">
      <div className="mb-2 flex items-center gap-1 px-1">
        <span className="flex-1 font-medium text-fg-3 text-xs">
          {total} item{total === 1 ? '' : 's'}
        </span>
        {(['all', 'notes'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={clsx(
              'rounded-lg px-2 py-1 text-xs',
              filter === f ? 'bg-accent-soft text-accent' : 'text-fg-3 hover:bg-surface-3',
            )}
          >
            {f === 'all' ? 'All' : 'Comments'}
          </button>
        ))}
      </div>
      {!total && (
        <div className="flex flex-col items-center gap-2 p-8 text-center text-fg-3">
          <MessageSquareText className="size-8 opacity-50" />
          <p className="text-sm">Highlights, notes and drawings you add will appear here.</p>
        </div>
      )}
      {groups.map((g) => (
        <section key={g.page.id} className="mb-3">
          <h3 className="px-2 pb-1 font-semibold text-[11px] text-fg-3 uppercase tracking-wider">
            Page {g.index + 1}
          </h3>
          <ul className="flex flex-col gap-1">
            {g.anns.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => {
                    const ui = useUi.getState()
                    ui.setTool('select')
                    ui.select([a.id])
                    const y =
                      'y' in a
                        ? a.y
                        : 'rects' in a
                          ? (a.rects[0]?.y ?? 0)
                          : 'y1' in a
                            ? Math.min(a.y1, a.y2)
                            : a.type === 'ink'
                              ? (a.points[0]?.[1] ?? 0)
                              : 0
                    ui.goToPage(g.index, Math.max(0, y - 60))
                    if (closeOnPick) ui.toggleSidebar(false)
                  }}
                  className={clsx(
                    'flex w-full gap-2.5 rounded-xl p-2 text-left transition-colors',
                    selection.includes(a.id) ? 'bg-accent-soft' : 'hover:bg-surface-3',
                  )}
                >
                  <span className="mt-1 size-2.5 shrink-0 rounded-full" style={{ background: a.color }} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-xs">
                      <span className="font-medium">{TYPE_LABEL[a.type]}</span>
                      <span className="text-fg-3">
                        {new Date(a.updatedAt ?? a.createdAt).toLocaleDateString()}
                      </span>
                    </span>
                    {annText(a) && (
                      <span className="mt-0.5 line-clamp-3 block text-[12.5px] text-fg-2">{annText(a)}</span>
                    )}
                    {a.comment && (
                      <span className="mt-1 block rounded-lg bg-surface-2 px-2 py-1 text-[12.5px]">
                        {a.comment}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
